
import React, { useEffect, useRef, useState } from 'react'
import DetailContainer from '../MainPage/common/DetailContainer'
import { images } from '../common/images';
import { messages } from '../common/notification';
import { deviceControl, deviceDetails } from '../common/ApiCall';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ConfirmDialog from '../common/ConfirmDialog';
import { useLiveConnection } from '../common/useLiveConnection';

// `device` is the device document the dashboard already read from /details for
// this device; when it is given, the tile uses it rather than asking /details
// again. The tile only re-reads after switching the appliance on or off, when
// it needs the new state.
function ACStatusDetailContainer({ time, selectedDeviceSelectedDayData, deviceId, device, applianceName, showTime = true }) {
  const [deviceDetail, setDeviceDetails] = useState(device ?? {});
  const [loading, setLoading] = useState(false);
  // Switching a store's appliance on or off is asked about first
  // (ConfirmDialog), so a stray click on the tile does not do it.
  const [confirming, setConfirming] = useState(false);
  // The read after an on/off waits this long, so the device has had time to
  // switch and report its new state; read straight away it still showed the
  // old one.
  const READ_AFTER_CONTROL_MS = 1500;
  const readTimer = useRef(null);
  useEffect(() => () => clearTimeout(readTimer.current), []);
  const getDeviceDetailLater = () => {
    clearTimeout(readTimer.current);
    readTimer.current = setTimeout(getDeviceDetail, READ_AFTER_CONTROL_MS);
  };
  const getDeviceDetail = () => {
    deviceDetails(deviceId)
      .then((res) => {
        setDeviceDetails(res);
        setLoading(false)
      })
      .catch((err) => {
        setDeviceDetails({});
        setLoading(false)
      });
  }
  useEffect(() => {
    if (device) {
      setDeviceDetails(device);
      return;
    }
    if (selectedDeviceSelectedDayData.length == 0) return;
    getDeviceDetail()

  }, [deviceId, device]);

  // The tile shows the appliance as it is now, whatever date is picked: online
  // or offline from the live /connectionStatus check (the same one the Device
  // Status tile makes - they share the request), and on or off from the
  // device's current applianceState in /details.
  const liveConnection = useLiveConnection(deviceId, true, selectedDeviceSelectedDayData);

  if (selectedDeviceSelectedDayData.length == 0) return;

  // The day data's `connected` is the fallback when the live check fails.
  const isConnected = liveConnection.connected ?? selectedDeviceSelectedDayData[0].connected;
  const checking = liveConnection.checking;

  const value =   selectedDeviceSelectedDayData[0]?.deviceId?.startsWith("KP")?"On":!isConnected ? "Offline" : (deviceDetail.applianceState === 0 ? "Off" : "On")
  let nameObj = {
    'M280246': 'Ac Status',
    "M240054": 'Motion Status'
  }

  const turningOn = value === "Off";
  const applianceLabel = applianceName ? `${applianceName} (${deviceId})` : deviceId;

  const switchAppliance = () => {
    setConfirming(false);
    setLoading(true)
    deviceControl(deviceId, turningOn ? 100 : 0).then((res) => {
      if (res) {
        getDeviceDetailLater()
      } else {
        setLoading(false)
      }
    })
  };

  return (
    <>
    <DetailContainer
      name={nameObj[deviceId] ? nameObj[deviceId] : "Appliance Status"}
      value={loading || checking ? <div class="spinner-border" role="status">
        <span class="sr-only"></span>
      </div> : value}
      {...(!loading && !checking && {
        textColor: value === 'Off' || value === 'Offline' ? '#FF0000' : 'rgb(26, 200, 33)',
      })}
      OnClick={() => {
        // One switch at a time: ignore clicks while a command or its read is
        // still in progress, or while the connection is still being checked.
        // An offline appliance cannot be switched.
        if (loading || checking || value === 'Offline') return;
        setConfirming(true);
      }}
      styleValue={{ cursor: value === 'Offline' ? '' : 'pointer' }}
      // The power-button icon switches the appliance too.
      iconClickable={!loading && !checking && value !== 'Offline'}
      iconTitle={value === "Off" ? "Turn on" : "Turn off"}
      disabledHint={
        !loading && !checking && value === 'Offline'
          ? "Device is offline - you can't turn it on or off."
          : undefined
      }
      time={time}
      showTime={showTime}
      // Red power button while the appliance is off, matching the red "Off";
      // a grey, struck-through one while it cannot be reached.
      color={value === "Off" ? "rgba(220, 38, 38, 0.08)" : value === "Offline" ? "rgba(100, 116, 139, 0.10)" : "rgba(118, 190, 194, 0.10)"}
      borderColor={"#76BEC2"}
      image={value === "Off" ? images.tileApplianceStatusOff : value === "Offline" ? images.tileApplianceStatusOffline : images.tileApplianceStatus}
    />
    <ConfirmDialog
      open={confirming}
      // Turning off can stop something the store is using, so it gets the
      // red look.
      tone={turningOn ? "default" : "danger"}
      icon={PowerSettingsNewIcon}
      title={turningOn ? "Turn on appliance?" : "Turn off appliance?"}
      message={`${applianceLabel} will be switched ${turningOn ? "on" : "off"}.`}
      confirmLabel={"Ok"}
      onConfirm={switchAppliance}
      onCancel={() => setConfirming(false)}
    />
    </>
  )
}

export default ACStatusDetailContainer